import { Avatar } from '@heroui/react';
import React, { useState, useCallback } from 'react';
import AvatarEditor, { Position } from 'react-avatar-editor';

export interface UserImageProps {
  /** Reference to the AvatarEditor for accessing canvas */
  editorRef?: React.RefObject<AvatarEditor>;
  /** Image source - can be File, URL, or base64 */
  userImage: string | File;
  /** Scale factor for zoom */
  scale: number;
  /** Rotation angle in degrees */
  rotation: number;
  /** Whether user can edit/drag the image */
  allowEdit: boolean;
  /** Size of the editor */
  size?: number;
}

export const UserImage: React.FC<UserImageProps> = ({
  editorRef,
  userImage,
  scale,
  rotation,
  allowEdit,
  size = 250,
}) => {
  const [position, setPosition] = useState<Position>({ x: 0.5, y: 0.5 });

  const handlePositionChange = useCallback(
    (newPosition: Position) => {
      if (allowEdit) {
        setPosition(newPosition);
      }
    },
    [allowEdit]
  );

  // If no image is provided, show a placeholder
  if (!userImage) {
    return (
      <div className="flex items-center justify-center">
        <Avatar
          size="lg"
          className="w-[250px] h-[250px]"
          classNames={{
            base: 'bg-default-200',
            icon: 'text-default-500',
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center">
      <div className="relative">
        <AvatarEditor
          ref={editorRef}
          image={userImage}
          position={position}
          onPositionChange={handlePositionChange}
          width={size}
          height={size}
          border={10}
          borderRadius={size / 2} // Make it circular
          color={[255, 255, 255, 0.6]} // Semi-transparent white border
          scale={scale}
          rotate={rotation}
          style={{
            display: 'block',
            borderRadius: '50%',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          }}
        />

        {/* Editing Indicator */}
        {allowEdit && (
          <div className="absolute -top-2 -right-2 bg-primary text-white text-xs px-2 py-1 rounded-full">
            Editing
          </div>
        )}
      </div>
    </div>
  );
};
